import * as React from 'react';
import { Typography, Divider, Box, Grid, TextField, Snackbar, Alert } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useEffect, useState } from 'react';
import {
  selectQuarantineCheckStatus,
  selectQuarantineCheckErrorMessage,
  quarantineEventCheck,
  selectQuarantineEventValidationResponse,
  selectTransferQuarantineStatus,
  selectTransferQuarantineError,
  transferQuarantine,
  resetQuarantineState,
} from '../../app/slices/quarantineSlice';
import QuarantineDialogBox from './QuarantineDialogBox';
import { QuarantineEventValidationResponse } from '../../model/quarantineEventValidationResponse';
import {
  CAN_NOT_UPLOAD,
  CAN_NOT_UPLOAD_MSG,
  CHECK_FAILED,
  CONFIRM_TITLE,
  quarantineErrorMessages,
} from './QuarantinePageMessages';
import { useNavigate } from 'react-router-dom';
import { resetEventState } from '../../app/slices/eventsSlice';

interface IProps {
  eventId?: string;
  runReport: Function;
}

const ASSET_TRANSFER_MESSAGE: string = 'Uploading Files';

export default function QuarantineDataFix({ eventId, runReport }: IProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [newEventId, setNewEventId] = useState('');
  const quarantineCheckError = useAppSelector(selectQuarantineCheckErrorMessage);
  const quarantineCheckStatus = useAppSelector(selectQuarantineCheckStatus);
  const quarantineEventValidationResponse = useAppSelector(selectQuarantineEventValidationResponse);

  const transferQuarantineStatus = useAppSelector(selectTransferQuarantineStatus);
  const transferQuarantineError = useAppSelector(selectTransferQuarantineError);

  const [openDialog, setOpenDialog] = useState(false);
  const [showConfirmButton, setShowConfirmButton] = useState(false);

  const [heading, setheading] = useState(CHECK_FAILED);
  const [reason, setReason] = useState(CHECK_FAILED);

  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const closeAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlert(false);
  };

  const handleClose = () => {
    setNewEventId('')
    setOpenDialog(false);
    dispatch(resetQuarantineState());
  };

  useEffect(() => {
    if (quarantineCheckStatus === 'FAILED' && quarantineCheckError) {
      setOpenDialog(true);
      setShowConfirmButton(false);

      const errorMessage = quarantineErrorMessages.find((it) => quarantineCheckError?.includes(it.error));
      setheading(errorMessage?.heading || CHECK_FAILED);
      setReason(errorMessage?.detailReason || quarantineCheckError);
    } else if (quarantineCheckStatus === 'SUCCESS' && quarantineEventValidationResponse?.status === 'COMPLETE') {
      setOpenDialog(true);
      setShowConfirmButton(true);

      setheading(CONFIRM_TITLE);
      setReason(confirmationMessage(quarantineEventValidationResponse, eventId, newEventId));
    } else if (quarantineCheckStatus === 'SUCCESS' && quarantineEventValidationResponse?.status === 'FAILED') {
      setOpenDialog(true);
      setShowConfirmButton(false);

      setheading(CAN_NOT_UPLOAD);
      setReason(failureMessage(quarantineEventValidationResponse) || CAN_NOT_UPLOAD_MSG);
    }
  }, [quarantineCheckStatus, quarantineCheckError]);

  useEffect(() => {
    if (transferQuarantineStatus === 'FAILED' && transferQuarantineError) {
      setAlertMessage(transferQuarantineError);
      setAlert(true);
    } else if (transferQuarantineStatus === 'SUCCESS') {
      setAlertMessage(ASSET_TRANSFER_MESSAGE);
      setAlert(true);
      handleClose();
      // before loading the new event reset event state
      dispatch(resetEventState())
      // navigate to event details page
      navigate('/event-details/' + newEventId);
    }
  }, [transferQuarantineStatus, transferQuarantineError]);

  const quarantineCheck = (event: React.SyntheticEvent) => {
    event.preventDefault();
    dispatch(quarantineEventCheck({ oldEventId: eventId, newEventId: newEventId }));
  };

  const transferFiles = () => {
    dispatch(transferQuarantine({ oldEventId: eventId, newEventId: newEventId }));
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Divider />
        <form onSubmit={quarantineCheck} aria-label="transfer-quarantine-data">
          <Grid container alignItems="center" justifyContent="flex-start" spacing={2} paddingTop={6}>
            <Grid item md={5} sm={6} xs={12}>
              <Typography>Enter the Event ID that the selected file should be changed to: </Typography>
            </Grid>

            <Grid item md={2} sm={3} xs={12}>
              <TextField
                size="small"
                fullWidth
                value={newEventId}
                onChange={(e) => setNewEventId(e.target.value.trim().toUpperCase())}
                placeholder="Event ID"
                required
                label="Event ID"
              />
            </Grid>

            <Grid item md={3} sm={3} xs={12}>
              <LoadingButton
                fullWidth
                variant="contained"
                loading={quarantineCheckStatus === 'LOADING'}
                disabled={!eventId}
                endIcon={''}
                loadingPosition="end"
                type="submit"
              >
                Upload file and load
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
        <QuarantineDialogBox
          heading={heading}
          detailReason={reason}
          openDialog={openDialog}
          closeDialog={handleClose}
          showConfirmButton={showConfirmButton}
          confirm={transferFiles}
          status={transferQuarantineStatus}
        ></QuarantineDialogBox>
      </Box>
      {
        <Snackbar
          open={alert}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={closeAlert}
          data-testid={'snack-bar'}
        >
          {alertMessage === ASSET_TRANSFER_MESSAGE ? (
            <Alert severity="success">{alertMessage}</Alert>
          ) : (
            <Alert severity="error">{alertMessage}</Alert>
          )}
        </Snackbar>
      }
    </>
  );
}

export function confirmationMessage(
  quarantineEventValidationResponse?: QuarantineEventValidationResponse,
  oldEventId?: string,
  newEventId?: string
): string {
  let totalAssets = 0;
  quarantineEventValidationResponse?.quarantinePibCompleteDetails?.forEach((it) => (totalAssets += it.assetCount));
  return `Files from ${oldEventId} will now be updated to ${newEventId} ${quarantineEventValidationResponse?.subprogram} for ${quarantineEventValidationResponse?.accountName}.\n\n By continuing you will be uploading ${totalAssets} images to ${newEventId}. \n This process may take a few minutes to complete. \n You will need to go to Event Manager to complete and send the images to Prelab.`;
}

export function failureMessage(quarantineEventValidationResponse?: QuarantineEventValidationResponse): string {
  let combinedErrorMessages = '';
  quarantineEventValidationResponse?.quarantinePibCompleteDetails?.forEach((detail) => {
    if (detail.errorMessages) {
      const keyValueArray = Object.entries(detail.errorMessages);
      combinedErrorMessages += keyValueArray.map(([key, value]) => `${key.replace('_', ' ')}: ${value}\n`);
    }
  });
  return combinedErrorMessages;
}
